import { NavLink, useNavigate } from "react-router-dom";
import { Home, Calendar, CheckSquare, Users, BarChart3, Megaphone, LogOut, X, Settings, QrCode, Smartphone, CreditCard, Gem } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Wordmark, LogoMark } from "@/components/Logo";

const LINKS_OPERATE = [
  { to: "/app", end: true, label: "Dashboard", Icon: Home },
  { to: "/app/classes", label: "Classes", Icon: Calendar },
  { to: "/app/checkin", label: "Check-in (mobile)", Icon: Smartphone },
  { to: "/app/checkin-qr", label: "Self check-in QR", Icon: QrCode },
  { to: "/app/attendance", label: "Attendance", Icon: CheckSquare },
];
const LINKS_ROSTER = [
  { to: "/app/students", label: "Students", Icon: Users },
  { to: "/app/analytics", label: "Analytics", Icon: BarChart3 },
];
const LINKS_ENGAGE = [
  { to: "/app/announcements", label: "Announcements", Icon: Megaphone },
  { to: "/app/billing", label: "Billing", Icon: CreditCard },
];
const LINKS_ADMIN = (academyId) => [
  { to: "/app/team", label: "Team", Icon: Settings },
  { to: "/app/plan", label: "Subscription", Icon: Gem },
  { to: `/poster/${academyId || ""}`, label: "Sign-in poster", Icon: QrCode, external: true },
];

function Group({ title, links }) {
  return (
    <div className="mb-5">
      <div className="label-tiny px-4 mb-2">{title}</div>
      <div className="space-y-0.5">
        {links.map(({ to, end, label, Icon, external }) => external ? (
          <a key={to} href={to} target="_blank" rel="noopener noreferrer" className="sidebar-link" data-testid={`nav-${label.toLowerCase().replace(/\s+/g,'-')}`}>
            <Icon size={16} /> {label}
          </a>
        ) : (
          <NavLink key={to} to={to} end={end} className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} data-testid={`nav-${label.toLowerCase().replace(/\s+/g,'-')}`}>
            <Icon size={16} /> {label}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

export default function Sidebar({ onClose }) {
  const { user, logout } = useAuth();
  const academyName = user?.academy_name || "Your Academy";
  const initials = academyName.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase() || "RO";
  return (
    <aside className="border-r flex flex-col bg-[var(--bg)]" style={{ borderColor: "var(--border)", width: 220, height: "100vh", position: "sticky", top: 0 }}>
      <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
        <Wordmark size={15} />
        {onClose && <button onClick={onClose} className="md:hidden" data-testid="sidebar-close"><X size={18}/></button>}
      </div>
      <div className="px-3 py-4 border-b flex items-center gap-3" style={{ borderColor: "var(--border)" }} data-testid="sidebar-academy-header">
        <div className="logo-mark" style={{ background: "var(--bg-card)", color: "var(--green)", border: "1px solid var(--border)", fontSize: 13 }}>{initials}</div>
        <div className="min-w-0">
          <div className="font-semibold text-[13px] tracking-tight truncate" data-testid="sidebar-academy-name">{academyName}</div>
          <div className="label-tiny mt-0.5">Owner workspace</div>
        </div>
      </div>
      <nav className="flex-1 min-h-0 px-3 py-5 overflow-y-auto">
        <Group title="Operate" links={LINKS_OPERATE} />
        <Group title="Roster" links={LINKS_ROSTER} />
        <Group title="Engage" links={LINKS_ENGAGE} />
        <Group title="Admin" links={LINKS_ADMIN(user?.academy_id)} />
      </nav>
      <div className="p-3 border-t" style={{ borderColor: "var(--border)" }}>
        <button onClick={logout} className="sidebar-link w-full" data-testid="btn-logout">
          <LogOut size={16} /> Sign out
        </button>
      </div>
    </aside>
  );
}
